import type { UseQueryResult } from '@tanstack/react-query';
import { ChatSidebarList, EmptyState, Skeleton, cn } from '@wundo/ui';
import type { UserOverview } from '@/types/messages.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { PersonRow } from './PersonRow.tsx';
import { TryAgain } from './TryAgain.tsx';

export interface PersonListProps {
  users: UseQueryResult<PaginatedResponse<UserOverview>>;
  /** The person whose transcript is open. */
  userId: string | null;
  organisationId: string | undefined;
  onSelect: (userId: string) => void;
}

/** Everyone who talked to Wundo, and a search over every user. */
export function PersonList({ users, userId, organisationId, onSelect }: PersonListProps) {
  const rows = users.data?.data ?? [];

  return (
    <aside
      data-ui='person-list'
      className={cn(
        'flex w-72 shrink-0 flex-col border-r border-line max-mobile:w-full max-mobile:border-r-0',
        userId && 'max-mobile:hidden',
      )}
    >
      <div className='flex flex-col gap-3 border-b border-line bg-fill-faint p-4'>
        <UserPicker
          aria-label='Search all users'
          placeholder='Search all users…'
          organisationId={organisationId}
          value={null}
          onChange={(next: UserOption | null) => {
            if (next) onSelect(next.id);
          }}
        />
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto max-mobile:flex-none max-mobile:overflow-visible'>
        {users.isPending && <PeopleSkeleton />}

        {users.isError && (
          <EmptyState
            title='Could not load the people'
            description='Something went wrong while loading who talked to Wundo.'
            action={<TryAgain onClick={() => void users.refetch()} />}
          />
        )}

        {users.isSuccess && rows.length === 0 && (
          <EmptyState title='No conversations found' />
        )}

        <ChatSidebarList className='p-2'>
          {rows.map((row) => (
            <PersonRow
              key={row.id}
              row={row}
              current={row.id === userId}
              onSelect={() => onSelect(row.id)}
            />
          ))}
        </ChatSidebarList>
      </div>
    </aside>
  );
}

/* The people on their way: rows the shape of a name and the line under it. */
function PeopleSkeleton() {
  return (
    <ul aria-busy='true' aria-label='Loading people' className='flex flex-col p-2'>
      {['w-1/2', 'w-2/3', 'w-2/5', 'w-3/5', 'w-1/2', 'w-2/3'].map((width, index) => (
        <li key={index} className='flex flex-col gap-1.5 px-2.5 py-2.75'>
          <Skeleton className={cn('h-3.5', width)} />
          <Skeleton className='h-2.5 w-3/4' />
        </li>
      ))}
    </ul>
  );
}
